import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { useAnchoredPosition } from "hooks/useAnchoredPosition";
import { SelectPortalMenu } from "./SelectPortalMenu";
import type { SelectOption, SelectProps, MenuPosition } from "./types";

// Note: If dropdown positioning/flipping needs to be more sophisticated,
// consider using floating-ui/react

const MENU_MAX_HEIGHT = 240; // matches max-h-60

const defaultFilterOption = (input: string, option: SelectOption) =>
  option.label.toLowerCase().includes(input.toLowerCase());

export const Select = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  autoFilter = false,
  className,
  inputClassName,
  menuClassName,
  disabled = false,
  multiline = false,
  name,
  id,
  onBlur,
  onFocus,
}: SelectProps) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentValue = value ?? "";

  const menuPosition = useAnchoredPosition<MenuPosition>({
    isOpen,
    anchorRef: fieldRef,
    compute: (rect) => {
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement = spaceBelow < MENU_MAX_HEIGHT ? "top" : "bottom";
      return {
        top:
          placement === "top"
            ? rect.top + window.scrollY
            : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        placement,
      };
    },
  });

  const assignFieldRef = useCallback(
    (node: HTMLInputElement | HTMLTextAreaElement | null) => {
      fieldRef.current = node;
    },
    [],
  );

  const adjustFieldHeight = useCallback(() => {
    if (!multiline) return;
    const element = fieldRef.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, [multiline]);

  const filterOption = useMemo(() => {
    if (!autoFilter) return null;
    if (typeof autoFilter === "object" && autoFilter.filterOption) {
      return autoFilter.filterOption;
    }
    return defaultFilterOption;
  }, [autoFilter]);

  const filteredOptions = useMemo(() => {
    if (filterOption) {
      return options.filter((option) => filterOption(inputValue, option));
    }
    if (!inputValue) return options;
    const inputLower = inputValue.toLowerCase();
    const matchedOptions: SelectOption[] = [];
    const unmatchedOptions: SelectOption[] = [];
    for (const option of options) {
      const labelLower = option.label.toLowerCase();
      if (labelLower.includes(inputLower) || inputLower.includes(labelLower)) {
        matchedOptions.push(option);
      } else {
        unmatchedOptions.push(option);
      }
    }
    return [...matchedOptions, ...unmatchedOptions];
  }, [options, inputValue, filterOption]);

  const highlightedValue = useMemo(() => {
    const matchByValue = options.find(
      (option) => option.value === currentValue,
    );
    if (matchByValue) return matchByValue.value;

    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return undefined;

    const matchByLabel = options.find(
      (option) => option.label === trimmedInput,
    );
    return matchByLabel?.value;
  }, [options, currentValue, inputValue]);

  const handleSelect = (option: SelectOption) => {
    setInputValue(option.label);
    onChange?.(option.value);
    setIsOpen(false);
    fieldRef.current?.focus();
  };

  const handleInputChange = (nextValue: string) => {
    setInputValue(nextValue);
    onChange?.(nextValue);
    setIsOpen(true);
  };

  // Keep the value single-line: Enter confirms instead of inserting a newline.
  const handleFieldKeyDown = (
    event: ReactKeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  useLayoutEffect(() => {
    adjustFieldHeight();
  }, [inputValue, adjustFieldHeight]);

  useEffect(() => {
    const match = options.find((option) => option.value === currentValue);
    if (match) {
      setInputValue(match.label);
      return;
    }
    setInputValue(currentValue);
  }, [currentValue, options]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const isClickInContainer =
        containerRef.current &&
        containerRef.current.contains(event.target as Node);
      const isClickInMenu =
        menuRef.current && menuRef.current.contains(event.target as Node);

      if (!isClickInContainer && !isClickInMenu) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const fieldClassName = clsx(
    "w-full bg-transparent pl-2 pr-6 py-1 text-sm text-gray-900",
    "border-none outline-none focus:ring-0 focus:shadow-none",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    inputClassName,
  );

  return (
    <div
      ref={containerRef}
      className={clsx(
        "relative rounded-md border border-gray-200 bg-white",
        "focus-within:border-blue-400",
        className,
      )}
    >
      <div className="relative">
        {multiline ? (
          <textarea
            ref={assignFieldRef}
            id={id}
            name={name}
            rows={1}
            value={inputValue}
            placeholder={placeholder}
            onFocus={(event) => {
              setIsOpen(true);
              onFocus?.(event);
            }}
            onBlur={onBlur}
            onChange={(event) => handleInputChange(event.target.value)}
            onKeyDown={handleFieldKeyDown}
            disabled={disabled}
            className={clsx(
              fieldClassName,
              "block max-h-40 resize-none overflow-y-auto",
              // whitespace-normal: collapse the value's newlines (textarea preserves them by default)
              "whitespace-normal",
            )}
          />
        ) : (
          <input
            ref={assignFieldRef}
            id={id}
            name={name}
            value={inputValue}
            placeholder={placeholder}
            onFocus={(event) => {
              setIsOpen(true);
              onFocus?.(event);
            }}
            onBlur={onBlur}
            onChange={(event) => handleInputChange(event.target.value)}
            disabled={disabled}
            className={fieldClassName}
          />
        )}
        <button
          type="button"
          aria-label="Toggle options"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setIsOpen((prev) => !prev)}
          disabled={disabled}
          className={clsx(
            "absolute right-1 flex items-center text-gray-400",
            multiline ? "top-1.5" : "inset-y-0",
          )}
        >
          <ChevronDown
            size={16}
            className={clsx("transition-transform", isOpen && "rotate-180")}
          />
        </button>
      </div>
      {isOpen && menuPosition && (
        <SelectPortalMenu
          isOpen={isOpen}
          position={menuPosition}
          filteredOptions={filteredOptions}
          highlightedValue={highlightedValue}
          onSelect={handleSelect}
          menuClassName={menuClassName}
          menuRef={menuRef}
        />
      )}
    </div>
  );
};
